import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  RequestUtilizationEfficiencyReportsInterFace,
  UtilizationEfficiencyReportsInterFace,
} from '../../../interface/utilization-efficiency-report-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import {
  disableUtilizationEfficiencyReportsFiltersApplied,
  fetchUtilizationEfficiencyReports,
  fetchListUtilizationEfficiencyReportsFilters,
  updateUtilizationEfficiencyReportsFilters,
  setSortData,
} from '../../../redux/slice/UtilizationEfficiencyReportSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { changeDateTimeFormat } from '../../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import stationManagement from '../../../utils/api/station-management';

interface ReportLeftTopContent {
  leftTopContent?: JSX.Element;
}
/**
 * UtilizationEfficiencyReport
 * @param {ReportLeftTopContent} properties The component properties.
 * @returns {any} The UtilizationEfficiencyReport component.
 */
function UtilizationEfficiencyReport(properties: ReportLeftTopContent) {
  const { leftTopContent } = properties;
  const { t } = useTranslation('stationManagement');
  const branding = useAppBranding();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    UtilizationEfficiencyReports,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.UtilizationEfficiencyReport);

  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const column = [
    {
      label: t('dateRange'),
      accessorKey: 'created_at' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: false,
      /**
       * Date Range (earliest to latest session time)
       */
      customData: (currentRow: UtilizationEfficiencyReportsInterFace) => (
        <p>
          {changeDateTimeFormat(currentRow?.earliestCreatedAt)} to{' '}
          {changeDateTimeFormat(currentRow?.latestCreatedAt)}
        </p>
      ),
    },
    {
      label: t('deviceSn'),
      accessorKey: 'chargingPointSn' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
    },
    {
      label: t('utilizationPercent'),
      accessorKey: 'utilizationPercentage' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
    },
    {
      label: t('idleTimeRatio'),
      accessorKey: 'idleTime' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
      /**
       * Custom Data for Idle Time Ratio
       * @param currentRow
       * @returns
       */
      customData: (currentRow: UtilizationEfficiencyReportsInterFace) => (
        <p>{ToFixedValue(currentRow?.idleTime)}</p>
      ),
    },
    {
      label: t('peakVsOffPeakSessionRatio'),
      accessorKey: 'offPeakSessions' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
      /**
       * peakSessions
       */
      customData: (UtilizationEfficiency: UtilizationEfficiencyReportsInterFace) => (
        <p>
          {UtilizationEfficiency.peakSessions}-{UtilizationEfficiency.offPeakSessions}
        </p>
      ),
    },
    {
      label: t('sessionsPerConnector'),
      accessorKey: 'totalSessionsPerConnector' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
    },
    {
      label: t('revenuePerKwh'),
      accessorKey: 'totalRevenuePerConnector' as keyof UtilizationEfficiencyReportsInterFace,
      sortAllow: true,
      /**
       * Format revenue per kWh
       */
      customData: (currentRow: UtilizationEfficiencyReportsInterFace) => {
        return (
          <div className="amount-in-transaction-table">
            <p className="table-row-each-data">
              {ToFixedValue(currentRow?.totalRevenuePerConnector)}
            </p>
            <span>{branding.text.currency}/kWh</span>
          </div>
        );
      },
    },
  ];

  /**
   * Handle the fetch the data in api function
   * @param {RequestUtilizationEfficiencyReportsInterFace} data The requested data parameters.
   */
  const fetchData = (data: RequestUtilizationEfficiencyReportsInterFace) => {
    if (!loading && selectedChargingPoint?.id) {
      dispatch(
        fetchUtilizationEfficiencyReports({ ...data, chargingStation: selectedChargingPoint.id }),
      );
    }
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('dateRange'), accessorKey: 'created_at' },
    { label: t('deviceSn'), accessorKey: 'chargingPointSn' },
    { label: t('utilization'), accessorKey: 'utilizationPercentage' },
    { label: t('idleTimeRatio'), accessorKey: 'idleTime' },
    { label: t('peakVsOffPeakSessionRatio'), accessorKey: '__peakOffPeak__' },
    { label: t('sessionsPerConnector'), accessorKey: 'totalSessionsPerConnector' },
    { label: t('revenuePerKwh'), accessorKey: 'totalRevenuePerConnector' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = {
      ...queryParameters,
      chargingStation: selectedChargingPoint?.id,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    stationManagement
      .fetchReportUtilizationExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map(
          (row: UtilizationEfficiencyReportsInterFace, index: number) =>
            exportCurrentColumns.map((col) => {
              switch (col.accessorKey) {
                case '__serial__':
                  return index + 1;
                case 'created_at':
                  return (
                    `${changeDateTimeFormat(row?.earliestCreatedAt)} to ${changeDateTimeFormat(row?.latestCreatedAt)}` ||
                    'N/A'
                  );
                case 'idleTime':
                  return ToFixedValue(row?.idleTime);
                case '__peakOffPeak__':
                  // Match table display: "peakSessions - offPeakSessions"
                  if (row?.peakSessions == null && row?.offPeakSessions == null) {
                    return 'N/A';
                  }
                  return `${row?.peakSessions ?? 0} - ${row?.offPeakSessions ?? 0}`;
                default:
                  return (row as any)[col?.accessorKey] || 'N/A';
              }
            }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-station-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-station-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      sortBy,
      sortField,
    });
  };

  /**
   * Function for sorting table data
   * @param {object} sortData The sort instructions.
   * @param {'asc' | 'desc'} sortData.sortBy The sort direction.
   * @param {string} sortData.sortField The sort field column.
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle page change
   * @param {number} pageNumber The selected page.
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: pageNumber,
      limit,
      sortBy,
      sortField,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('UtilizationEfficiencyReportsFilters'); // string | null

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchListUtilizationEfficiencyReportsFilters({ chargingStation: currentId }));
    }
    const queryParameters = GetParametersFromCommonFilters(filters);

    if (UtilizationEfficiencyReports.length === 0 || lastId !== currentId || isFilterUpdated) {
      fetchData({
        page,
        limit,
        ...queryParameters,
      });
      dispatch(disableUtilizationEfficiencyReportsFiltersApplied());
    }
    sessionStorage.setItem('UtilizationEfficiencyReportsFilters', currentId);
  }, [selectedChargingPoint?.id, filters]);

  return (
    <div>
      <CustomTable
        version="v2"
        currentPage={page}
        header={column}
        data={UtilizationEfficiencyReports}
        serialNo
        sizeChangeButtonRequired
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        filterListLoading={filterListLoading}
        filters={filters}
        filtersToDispatch={updateUtilizationEfficiencyReportsFilters}
        filterListToDispatch={fetchListUtilizationEfficiencyReportsFilters}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        refreshCurrentPages={handleRefresh}
        leftTopContent={leftTopContent}
        handlePageChange={handlePageChange}
        handleLimitChange={handleLimitChange}
        topHeader={false}
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        paginate
        flexDirection
        onHiddenColumnsChange={setHiddenColumns}
        tableHeight="calc(100vh - 340px)"
      />
    </div>
  );
}

export default UtilizationEfficiencyReport;
